import { CertificateData, ISODateTime } from '@yandex-int/k-common';
import { LOADING_STATUSES } from '@yandex-int/k-common/client/api/constants';
import { uniq } from 'lodash';
import { getPortfolioConfig } from 'platform-components/src/components/app-factory/selectors';
import {
    getCertificateExpandedSuggestions,
    getCertificateSuggestions,
    getFullName,
} from 'platform-components/src/components/user/selectors';
import { createSelector } from 'reselect';

import { CERTIFICATE_PROJECTS_SLUGS } from 'common.components/certificate-card/constants';
import {
    getAllCertSlugsWithConditionFromConfig,
    getAllCertSlugsWithoutConditionFromConfig,
} from 'common.components/certificate-card/selectors';
import { CertificatesExpandedParamsModalData } from 'common.components/certificates-expanded-params-modal/typings';
import { CertificatesParamsModalData } from 'common.components/certificates-params-modal/certificates-params-modal';
import {
    getGradesLevels,
    getLocalityName,
    getSchoolName,
    getIsInBaseCertificateExperiment,
    getEventCodesWithDates,
    getCertExpandedParamsFromViewedHints,
} from 'common.components/user/selectors';
import { FULL_CLASS_GRADES } from 'common.components/user/user.constants';

import { ReduxTeacherCertificatesStore, TeacherCertificatesStoreState } from './typings';
import { getSlugFromEventCode } from './utils';

/** слаг серта инноватора, который ранее генерился в админке */
const INNOVATOR_CERT_SLUG = 'base';
const INNOVATOR_PROJECT_SLUG = 'innovator';

const MS_IN_DAY = 24 * 60 * 60 * 1000;
const PERIOD_IN_DAYS = 20;

const certificatesSelector = (storeState: ReduxTeacherCertificatesStore): TeacherCertificatesStoreState =>
    storeState?.teacherCertificates;

export const getReadyCertificates = createSelector(
    certificatesSelector,
    (storeState: TeacherCertificatesStoreState): CertificateData[] => storeState.readyCertificates
);

export const getReadyCertificatesLoadingStatus = createSelector(
    certificatesSelector,
    (storeState: TeacherCertificatesStoreState): LOADING_STATUSES => storeState.readyCertificatesLoadingStatus
);

export const getAllAvailableCertSlugs = createSelector(
    [getAllCertSlugsWithConditionFromConfig, getAllCertSlugsWithoutConditionFromConfig],
    (allAvailableSlugsWithCondition, allAvailableSlugsWithoutCondition) => [
        ...allAvailableSlugsWithCondition,
        ...allAvailableSlugsWithoutCondition,
    ]
);

/** Получаем слаги всех благодарственых писем из активных проектов, которые подходят по классам учителя */
const getSlugsForThankYouLettersFromActiveProjects = createSelector(
    [getGradesLevels, getPortfolioConfig],
    (teacherGrades, portfolioConfig) => {
        const activeProjects = portfolioConfig?.active_projects;
        const grades = portfolioConfig?.grades;
        const configSlugs = portfolioConfig?.cert_slugs;
        let slugs: string[] = [];

        if (activeProjects && grades && configSlugs) {
            const activeThankYouLettersProjects = activeProjects.filter(
                (project) => !CERTIFICATE_PROJECTS_SLUGS.includes(project) && project !== INNOVATOR_PROJECT_SLUG
            );

            const matchProjectsByGrades = activeThankYouLettersProjects?.filter((project) => {
                const activeGrades = (grades && grades[project]) || FULL_CLASS_GRADES;

                return activeGrades && teacherGrades.some((grade) => activeGrades.includes(grade));
            });

            if (matchProjectsByGrades.length > 0 && activeThankYouLettersProjects) {
                slugs = matchProjectsByGrades.reduce(
                    (acc, projectSlug) => [...acc, ...configSlugs[projectSlug]],
                    slugs
                );
            }
        }

        return uniq(slugs);
    }
);

/**
 * Получаем слаги всех благодарственых писем из всех проектов в конфиге,
 * чтобы отрисовать полученные ранее сертификаты
 */
const getSlugsForAllThankYouLetters = createSelector([getPortfolioConfig], (portfolioConfig) => {
    const configSlugs = portfolioConfig?.cert_slugs;
    const slugs: string[] = [];

    if (configSlugs) {
        for (const project in configSlugs) {
            if (project !== INNOVATOR_PROJECT_SLUG && !CERTIFICATE_PROJECTS_SLUGS.includes(project)) {
                slugs.push(...configSlugs[project]);
            }
        }
    }

    return uniq(slugs);
});

/** Получаем "очищенные" слаги из саджестов, которые применимы для всех сертов, которые открываются по коду */
const getSlugsFromSuggestions = createSelector(
    [getCertificateSuggestions, getAllAvailableCertSlugs],
    (certificateSuggestions, allAvailableCertSlugs) => {
        const slugsFromSuggestions = certificateSuggestions
            .map((suggestion) => getSlugFromEventCode(suggestion, allAvailableCertSlugs))
            .filter(Boolean) as string[];

        return uniq(slugsFromSuggestions);
    }
);

const getOpenedThankYouLetters = createSelector(
    [getCertificateSuggestions, getSlugsForAllThankYouLetters],
    (certificateSuggestions, slugsForAllThankYouLetters) => {
        const thankYouLetterSlugsFromSuggestions = certificateSuggestions
            .map((suggestion) => getSlugFromEventCode(suggestion, slugsForAllThankYouLetters))
            .filter(Boolean) as string[];

        return uniq(thankYouLetterSlugsFromSuggestions);
    }
);

/**
 * Получаем слаги из очищенных саджестов, которые есть только
 * в наборе слагов из сертов, которые открываются по условию
 */
const getSlugsFromSuggestionsForCertsWithCondition = createSelector(
    [getSlugsFromSuggestions, getAllCertSlugsWithConditionFromConfig],
    (suggestionSlugs, allAvailableSlugsWithCondition) => {
        const slugsForCertsWithConditionFromSuggestions = suggestionSlugs
            .map((suggestion) => getSlugFromEventCode(suggestion, allAvailableSlugsWithCondition))
            .filter(Boolean) as string[];

        return uniq(slugsForCertsWithConditionFromSuggestions);
    }
);

/**
 * Оставляем только те введенные коды, которые совпадают со слагами сертов по условию
 * Убираем в их слагах все лишнее (цифры и т.д.)
 */
const getValidEnteredCodesForCertsWithCondition = createSelector(
    [getEventCodesWithDates, getAllCertSlugsWithConditionFromConfig],
    (allEnteredEvents, allAvailableSlugsWithCondition) => {
        const validEnteredCodesForCertsWithCondition: { event_code: string; date_created: ISODateTime }[] = [];

        allEnteredEvents.forEach((event) => {
            const code = event.event_code;

            const eventCodeSlug = getSlugFromEventCode(code, allAvailableSlugsWithCondition);

            if (eventCodeSlug) {
                validEnteredCodesForCertsWithCondition.push({
                    event_code: eventCodeSlug,
                    date_created: event.date_created,
                });
            }
        });

        return validEnteredCodesForCertsWithCondition;
    }
);

/** Получаем слаги из введенных кодов, которые совпадают с саджестами - это открытые серты по условию */
const getSlugsForOpenCertsWithCondition = createSelector(
    [getValidEnteredCodesForCertsWithCondition, getSlugsFromSuggestionsForCertsWithCondition],
    (enteredEventCodes, suggestionSlugs) => {
        const slugsForOpenedCertsWithCondition = enteredEventCodes
            .map((event) => getSlugFromEventCode(event.event_code, suggestionSlugs))
            .filter(Boolean) as string[];

        return uniq(slugsForOpenedCertsWithCondition);
    }
);

/**
 * Получаем слаги из введенных кодов, которые не совпадают с саджестами и не протухли
 * - это закрытые серты, доступные по условию
 */
const getSlugsForClosedCertsWithCondition = createSelector(
    [getValidEnteredCodesForCertsWithCondition, getSlugsFromSuggestionsForCertsWithCondition],
    (enteredEventCodes, suggestionSlugs) => {
        const now = new Date();
        const currentTime = now.getTime();

        const slugsForClosedCertsWithCondition = enteredEventCodes
            .map((event) => {
                const code = event.event_code;

                const eventDateTime = new Date(event.date_created).getTime();

                const eventSlug = getSlugFromEventCode(code, suggestionSlugs);

                return !eventSlug && currentTime - eventDateTime < PERIOD_IN_DAYS * MS_IN_DAY ? code : null;
            })
            .filter(Boolean) as string[];

        return uniq(slugsForClosedCertsWithCondition);
    }
);

/** Получаем слаги для открытых сертов без условий */
const getSlugsForOpenCertsWithoutCondition = createSelector(
    [getEventCodesWithDates, getAllCertSlugsWithoutConditionFromConfig],
    (allEnteredEvents, allAvailableSlugsWithoutCondition) => {
        const validEnteredCodesForCertsWithoutCondition = allEnteredEvents
            .map((event) => getSlugFromEventCode(event.event_code, allAvailableSlugsWithoutCondition))
            .filter(Boolean) as string[];

        return uniq(validEnteredCodesForCertsWithoutCondition);
    }
);

export const getCertificatesParamsLoadingStatus = createSelector(
    certificatesSelector,
    (storeState): LOADING_STATUSES => storeState.certificatesParamsLoadingStatus
);

const getOpenInnovatorCertSlug = createSelector(
    [getCertificateSuggestions, getReadyCertificates],
    (certificateSuggestions, readyCertificates) => {
        const readyCertificateSlugs = readyCertificates.map((readyCertParams) => readyCertParams.slug);
        return getSlugFromEventCode(INNOVATOR_CERT_SLUG, [...certificateSuggestions, ...readyCertificateSlugs]);
    }
);

export const getExpandedCertificatesSlugs = createSelector(
    [getPortfolioConfig],
    (portfolioConfig) => portfolioConfig?.expanded_slugs || []
);

export const getOpenedExpandedCertificatesSlugs = createSelector(
    [getCertificateExpandedSuggestions],
    (expandedCertificateSuggestions) => Object.keys(expandedCertificateSuggestions)
);

const getClosedThankYouLetters = createSelector(
    [getSlugsForThankYouLettersFromActiveProjects, getOpenedThankYouLetters, getOpenedExpandedCertificatesSlugs],
    (slugsForThankYouLettersFromActiveProjects, openedThankYouLetters, openedExpandedCertificatesSlugs) => {
        const allOpenedThankYouLetters = new Set(openedThankYouLetters.concat(openedExpandedCertificatesSlugs));

        const closedThankYouLetterSlugs = slugsForThankYouLettersFromActiveProjects.filter(
            (thankYouLetterSlug) => !allOpenedThankYouLetters.has(thankYouLetterSlug)
        );

        return uniq(closedThankYouLetterSlugs);
    }
);

export const getLockedCertificatesSlugs = createSelector(
    [
        getClosedThankYouLetters,
        getSlugsForClosedCertsWithCondition,
        getOpenInnovatorCertSlug,
        getIsInBaseCertificateExperiment,
    ],
    (closedThankYouLetters, closedCertsWithCondition, openInnovatorCertSlug, isInBaseCertificateExperiment) => {
        const lockedCertSlugs = [...closedThankYouLetters, ...closedCertsWithCondition];

        if (!openInnovatorCertSlug && !isInBaseCertificateExperiment) {
            return uniq([INNOVATOR_CERT_SLUG, ...lockedCertSlugs]);
        }

        return uniq(lockedCertSlugs);
    }
);

export const getOpenCertificates = createSelector(
    [
        getOpenInnovatorCertSlug,
        getReadyCertificatesLoadingStatus,
        getOpenedThankYouLetters,
        getSlugsForOpenCertsWithCondition,
        getSlugsForOpenCertsWithoutCondition,
        getOpenedExpandedCertificatesSlugs,
    ],
    (
        openInnovatorCertSlug,
        loadingStatus,
        openThankYouLettersSlugs,
        openCertSlugsWithCondition,
        openCertSlugsWithoutCondition,
        openedExpandedSlugs
        // eslint-disable-next-line max-params
    ) => {
        let openCertSlugs: string[] = [];

        if (loadingStatus === LOADING_STATUSES.SUCCESS) {
            if (openInnovatorCertSlug) {
                openCertSlugs.push(openInnovatorCertSlug);
            }

            openCertSlugs = [
                ...openCertSlugs,
                ...openThankYouLettersSlugs,
                ...openCertSlugsWithCondition,
                ...openCertSlugsWithoutCondition,
                ...openedExpandedSlugs,
            ];
        }

        return uniq(openCertSlugs);
    }
);

export const getInitialCertificatesData = createSelector(
    [getFullName, getSchoolName, getLocalityName],
    (fullName, schoolName, localityName): CertificatesParamsModalData => ({
        fullName: fullName,
        school: schoolName || '',
        locality: localityName || '',
    })
);

export const getInitialExpandedCertificatesData = createSelector(
    [getInitialCertificatesData, getCertExpandedParamsFromViewedHints],
    // eslint-disable-next-line complexity
    (defaultCertificatesData, certGenParams): CertificatesExpandedParamsModalData => ({
        fullName: defaultCertificatesData.fullName || '',
        school: defaultCertificatesData.school || '',
        locality: defaultCertificatesData.locality || '',
        need_paper_variant: certGenParams?.need_paper_variant || '',
        residence_address: certGenParams?.residence_address || null,
        snls: certGenParams?.snls || '',
        date_of_birth: certGenParams?.date_of_birth || '',
        gender: certGenParams?.gender || '',
        citizenship_ru: certGenParams?.citizenship_ru || '',
        citizenship_others: certGenParams?.citizenship_others || null,
        has_higher_education: certGenParams?.has_higher_education || '',
    })
);
